<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="milestones-page__title m-0">
          {{ $t('UserProjects.milestones.pageTitle') }}
        </h1>
        <p v-if="selectedProject?.name" class="milestones-page__subline m-0">
          {{ selectedProject.name }}
        </p>
      </div>
      <div v-if="userProjectContextReady && !loading" class="flex items-center gap-3 shrink-0">
        <button
          type="button"
          class="milestones-page__csv-btn"
          data-testid="milestones-csv-import"
          @click="onCsvImportClick"
        >
          <i class="pi pi-upload text-sm" aria-hidden="true" />
          {{ $t('UserProjects.milestones.csvImport') }}
        </button>
        <Button
          :label="$t('UserProjects.milestones.createShort')"
          icon="pi pi-plus"
          class="btn-primary-accent"
          unstyled
          data-testid="milestones-create"
          @click="openCreateDialog"
        />
      </div>
    </div>

    <Message v-if="!hasProjectContext && userProjectContextReady" severity="info" :closable="false">
      {{ $t('UserProjects.context.required') }}
    </Message>

    <Message v-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <Message
      v-if="showForecastSuggestionBanner"
      severity="warn"
      :closable="false"
      data-testid="milestones-forecast-suggestion-banner"
    >
      <span>{{ $t('UserProjects.milestones.forecastSuggestion.banner') }}</span>
      <span v-if="suggestedMilestone" class="font-semibold ml-1">
        {{ suggestedMilestone.title }}
      </span>
    </Message>

    <div
      v-if="hasProjectContext && userProjectContextReady && !loading"
      class="flex flex-wrap items-center gap-2"
      data-testid="milestones-status-filters"
    >
      <button
        v-for="option in statusFilterOptions"
        :key="option.value"
        type="button"
        class="milestones-page__pill"
        :class="{ 'milestones-page__pill--active': statusFilter === option.value }"
        :data-testid="`milestones-filter-${option.value}`"
        @click="statusFilter = option.value"
      >
        {{ option.label }}
      </button>
    </div>

    <div v-if="loading || !userProjectContextReady" class="flex items-center justify-center py-16 text-[var(--color-disabled)]">
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('UserProjects.milestones.loading') }}
    </div>

    <div
      v-else-if="hasProjectContext && isEmpty"
      class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
    >
      <div class="font-medium text-[var(--color-dark)]">{{ $t('UserProjects.milestones.emptyTitle') }}</div>
      <div class="text-sm mt-1">{{ $t('UserProjects.milestones.emptyHint') }}</div>
    </div>

    <div
      v-else-if="hasProjectContext && filteredMilestones.length === 0"
      class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
    >
      <div class="font-medium text-[var(--color-dark)]">{{ $t('UserProjects.milestones.noMatchesTitle') }}</div>
      <div class="text-sm mt-1">{{ $t('UserProjects.milestones.noMatchesHint') }}</div>
    </div>

    <div
      v-else-if="hasProjectContext"
      class="mt-2"
      data-testid="milestones-timeline-list"
    >
      <MilestoneTimelineCard
        v-for="(milestone, index) in filteredMilestones"
        :key="milestone.id"
        :milestone="milestone"
        :is-last="index === filteredMilestones.length - 1"
        :editable="isEditable(milestone)"
        :responsible-name="milestone.responsibleDisplayName || ''"
        :constraint-count="milestone.constraintCount ?? 0"
        @edit="openEditDialog"
        @mark-done="markDone"
        @delete="confirmDelete"
      />
    </div>

    <MilestoneFormDialog
      v-model:visible="dialogVisible"
      :mode="dialogMode"
      :initial-milestone="editingMilestone"
      :open-constraints="openConstraints"
      :constraints-loading="constraintsLoading"
      :saving="saving"
      @submit="submitForm"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Message from 'primevue/message';
import MilestoneTimelineCard from '@/components/CustomerApp/UserProjects/MilestoneTimelineCard.vue';
import MilestoneFormDialog from '@/components/CustomerApp/UserProjects/MilestoneFormDialog.vue';
import {
  filterMilestonesByAmpel,
  sortMilestonesForTimeline,
} from '@/utils/milestoneDisplay';
import {
  hasSuggestedAtRiskBanner,
  suggestedAtRiskMilestone,
} from '@/utils/milestoneForecastHint';

const EDITABLE_STATUSES = new Set(['open', 'on_track', 'at_risk']);

export default {
  name: 'UserProjectsMilestonesView',
  components: {
    Button,
    Message,
    MilestoneTimelineCard,
    MilestoneFormDialog,
  },
  setup() {
    return {
      confirm: useConfirm(),
      toast: useToast(),
    };
  },
  data() {
    return {
      dialogVisible: false,
      dialogMode: 'create',
      editingMilestone: null,
      saving: false,
      statusFilter: 'all',
    };
  },
  computed: {
    ...mapGetters('pmMilestone', {
      milestones: 'milestones',
      loading: 'loading',
      error: 'error',
      isEmpty: 'isEmpty',
    }),
    ...mapGetters('pmConstraint', {
      openConstraints: 'openConstraints',
      constraintsLoading: 'loading',
    }),
    ...mapGetters('pmProject', {
      selectedProjectId: 'selectedProjectId',
      selectedProject: 'selectedProject',
      hasProjectContext: 'hasUserProjectContext',
      userProjectContextReady: 'userProjectContextReady',
    }),
    statusFilterOptions() {
      return [
        { value: 'all', label: this.$t('UserProjects.milestones.filters.all') },
        { value: 'green', label: this.$t('UserProjects.milestones.filters.green') },
        { value: 'yellow', label: this.$t('UserProjects.milestones.filters.yellow') },
        { value: 'red', label: this.$t('UserProjects.milestones.filters.red') },
      ];
    },
    sortedMilestones() {
      return sortMilestonesForTimeline(this.milestones || []);
    },
    filteredMilestones() {
      return filterMilestonesByAmpel(this.sortedMilestones, this.statusFilter);
    },
    showForecastSuggestionBanner() {
      return this.hasProjectContext && hasSuggestedAtRiskBanner(this.milestones);
    },
    suggestedMilestone() {
      return suggestedAtRiskMilestone(this.milestones);
    },
  },
  watch: {
    selectedProjectId() {
      this.statusFilter = 'all';
      this.refresh();
    },
    userProjectContextReady(isReady) {
      if (isReady) {
        this.refresh();
      }
    },
  },
  async mounted() {
    if (this.userProjectContextReady) {
      await this.refresh();
    }
    if (this.$route.query.create === '1' && this.hasProjectContext) {
      this.openCreateDialog();
      this.$router.replace({ path: this.$route.path, query: {} });
    }
    const routeFilter = this.$route.query.filter;
    if (typeof routeFilter === 'string' && ['all', 'green', 'yellow', 'red'].includes(routeFilter)) {
      this.statusFilter = routeFilter;
    }
  },
  methods: {
    ...mapActions('pmMilestone', [
      'fetchMilestones',
      'setProjectContext',
      'fetchMilestoneDetail',
      'createMilestone',
      'updateMilestone',
      'updateMilestoneStatus',
      'deleteMilestone',
    ]),
    ...mapActions('pmConstraint', ['fetchConstraints']),
    ...mapActions('pmCommitment', ['fetchUserProjectsOverview']),

    isEditable(milestone) {
      return EDITABLE_STATUSES.has(milestone.status);
    },

    onCsvImportClick() {
      this.toast.add({
        severity: 'info',
        summary: this.$t('UserProjects.milestones.csvImport'),
        detail: this.$t('UserProjects.milestones.csvImportDeferred'),
        life: 4000,
      });
    },

    async refresh() {
      if (!this.userProjectContextReady) return;
      try {
        if (this.selectedProjectId) {
          await this.setProjectContext(this.selectedProjectId);
        }
      } catch {
        // handled in store
      }
    },

    async refreshOverview() {
      try {
        await this.fetchUserProjectsOverview();
      } catch {
        // overview errors are surfaced on overview page
      }
    },

    async ensureConstraintsLoaded() {
      if (!this.selectedProjectId) return;
      try {
        await this.fetchConstraints();
      } catch {
        // constraint errors are non-blocking for milestone dialog
      }
    },

    openCreateDialog() {
      if (!this.hasProjectContext) {
        this.toast.add({
          severity: 'warn',
          summary: this.$t('UserProjects.milestones.emptyTitle'),
          detail: this.$t('UserProjects.context.required'),
          life: 4000,
        });
        return;
      }
      this.dialogMode = 'create';
      this.editingMilestone = null;
      this.ensureConstraintsLoaded();
      this.dialogVisible = true;
    },

    async openEditDialog(milestone) {
      this.dialogMode = 'edit';
      this.ensureConstraintsLoaded();
      try {
        this.editingMilestone = await this.fetchMilestoneDetail(milestone.id);
        this.dialogVisible = true;
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.milestones.toast.error'),
          life: 4000,
        });
      }
    },

    closeDialog() {
      this.dialogVisible = false;
      this.saving = false;
      this.editingMilestone = null;
    },

    async submitForm(payload) {
      this.saving = true;
      try {
        if (this.dialogMode === 'create') {
          await this.createMilestone(payload);
          this.toast.add({
            severity: 'success',
            summary: this.$t('ProjectManagement.milestones.toast.created'),
            life: 3000,
          });
        } else {
          await this.updateMilestone({
            id: this.editingMilestone.id,
            payload,
          });
          this.toast.add({
            severity: 'success',
            summary: this.$t('ProjectManagement.milestones.toast.updated'),
            life: 3000,
          });
        }
        this.closeDialog();
        await this.refreshOverview();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.milestones.toast.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },

    async markDone(milestone) {
      try {
        await this.updateMilestoneStatus({ id: milestone.id, status: 'done' });
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.milestones.toast.done'),
          life: 3000,
        });
        await this.refreshOverview();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.milestones.toast.error'),
          life: 4000,
        });
      }
    },

    confirmDelete(milestone) {
      this.confirm.require({
        message: this.$t('ProjectManagement.milestones.deleteConfirm', { name: milestone.title }),
        header: this.$t('ProjectManagement.milestones.deleteTitle'),
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await this.deleteMilestone(milestone.id);
            this.toast.add({
              severity: 'success',
              summary: this.$t('ProjectManagement.milestones.toast.deleted'),
              life: 3000,
            });
            await this.refreshOverview();
          } catch {
            this.toast.add({
              severity: 'error',
              summary: this.$t('ProjectManagement.milestones.toast.error'),
              life: 4000,
            });
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.milestones-page__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--color-dark);
}

.milestones-page__subline {
  margin-top: 0.125rem;
  font-size: 13px;
  color: var(--color-disabled);
}

.milestones-page__pill {
  padding: 0.375rem 0.875rem;
  border: none;
  border-radius: 999px;
  background: #f5f5f5;
  color: #6b6b6b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.milestones-page__pill:hover {
  background: #eeeeee;
}

.milestones-page__pill--active {
  background: linear-gradient(135deg, var(--color-orange) 0%, #f59e0b 100%);
  color: #fff;
}

.milestones-page__csv-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-orange);
  border-radius: 10px;
  background: #fff;
  color: var(--color-orange);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.milestones-page__csv-btn:hover {
  background: rgba(228, 130, 26, 0.05);
}
</style>
